import { useParams } from 'react-router-dom';
import { SIGN_IN_PATH } from 'app/route/paths';
import { FlexColumn, SvgIcon } from '@make-software/cspr-design';
import React, { useEffect, useState } from 'react';
import { DealListItemResponse, getDealByID } from 'app/console-api';
import { SkeletonLoader } from 'app/components/common/skeleton-loader/skeleton-loader';
import { DealViewDetails } from 'app/components/management/apps/deals/view/deal-view-details';
import { HttpError } from 'app/components/utils/http-error';
import { AUTH_TOKEN, customErrorMessage, statusCode } from 'app/constants';
import { ErrorResult } from 'app/organizations-api/types';
import { ErrorWindow } from 'app/components/common/error-window/error-window';
import NotFoundIcon from 'assets/logos/no-found.svg';
import { PageContainer, ViewPageContainer } from 'app/components/common/common-styles';
import { useTabsCtx } from 'app/components/management/apps/tabs-page-layout';
import { RewardsType } from 'app/console-api/apps/types';
import { getRewardsTypes } from 'app/console-api/apps/rewards';

export const DealViewPage = () => {
  const { appID } = useTabsCtx();
  const { id } = useParams<{ id: string }>();
  const [deal, setDeal] = React.useState<DealListItemResponse | undefined>(undefined);
  const [unexpectedError, setUnexpectedError] = useState<ErrorResult | null>(null);
  const { setBreadcrumbs } = useTabsCtx();
  const [rewardsTypes, setRewardsTypes] = useState<RewardsType[]>([]);

  React.useEffect(() => {
    if (!deal) return;
    setBreadcrumbs([
      {
        label: deal.dealDetails.title
      }
    ]);
    return () => setBreadcrumbs([]);
  }, [deal]);

  useEffect(() => {
    if (!id) {
      console.error('No ID provided in the route.');
      return;
    }
    getRewardsTypes(appID)
      .then((response) => setRewardsTypes(response.data || []))
      .catch((error: any) => {
        console.error('Failed to fetch rewards types', error);
      });
    getDealByID(appID, id)
      .then((response) => {
        setDeal(response);
      })
      .catch((error: any) => {
        if (error instanceof HttpError) {
          switch (error.statusCode) {
            case statusCode.validation_error:
              setUnexpectedError({
                code: error.statusCode.toString(),
                message: error.message
              });
              break;
            case statusCode.authentication_error:
              localStorage.removeItem(AUTH_TOKEN);
              window.location.href = SIGN_IN_PATH;
              return;
            case statusCode.unexpected_error:
              setUnexpectedError(customErrorMessage);
              break;
            default:
              setUnexpectedError(customErrorMessage);
              break;
          }
        } else {
          setUnexpectedError(customErrorMessage);
        }
        console.error('Failed to fetch deal by id:', error);
      });
  }, [id]);

  if (!deal) {
    return (
      <FlexColumn style={{ width: '100%' }} gap={32}>
        <PageContainer>
          <ViewPageContainer>
            <SkeletonLoader height={'600px'} />
          </ViewPageContainer>
        </PageContainer>
      </FlexColumn>
    );
  }

  if (unexpectedError) {
    return (
      <ErrorWindow
        withModal
        title={unexpectedError.code}
        information={unexpectedError.message}
        icon={<SvgIcon src={NotFoundIcon} width={219} height={125} />}
      />
    );
  }

  return (
    <FlexColumn style={{ width: '100%' }} gap={32}>
      <PageContainer>
        <DealViewDetails data={deal} rewardsTypes={rewardsTypes} />
      </PageContainer>
    </FlexColumn>
  );
};
